import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from '@router'
import Antd from 'ant-design-vue'
import 'ant-design-vue/dist/reset.css'
import './assets/styles/oleo-script-font.css'
import './assets/styles/zalando-sans-font.css'
import './styles/phone-frame-squircle-fallback.css'
import tokenService from '@utils/tokenService'
import { myGetCurrentUser, myLogin, isDevelopmentMode } from '@api/myUserApi'
import { useUserStore } from '@store/user'
import logger, { initSessionContext, initDevFileSink, initConsoleCapture, getSessionId } from '@utils/logger'
import uploader from 'vue-simple-uploader'
import { permission } from './directives/permission'
import permissionManager from '@utils/permissionManager'
import { initIosInputZoomFix } from '@utils/iosInputFix'
import { initMediaSession } from '@utils/mediaSessionManager'

// 观测 sessionId（与登录 session 无关），尽早初始化
const observabilitySessionId = initSessionContext()
initConsoleCapture()
initDevFileSink()

initIosInputZoomFix()

window.addEventListener('error', (event) => {
  logger.error('Global', 'Uncaught error', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: event.error?.stack,
  })
})

window.addEventListener('unhandledrejection', (event) => {
  logger.error('Global', 'Unhandled promise rejection', {
    reason: event.reason?.message || String(event.reason),
    stack: event.reason?.stack,
  })
})

// 阿里云音频缓存已改为 SmartCacheManager 整文件下载（播放 ≥50% 或 ≥15s 后触发），不再使用 SW Range 缓存

const app = createApp(App)
const pinia = createPinia()

app.config.errorHandler = (err, instance, info) => {
  logger.error('Vue', 'Component error', {
    message: err?.message,
    info,
    stack: err?.stack,
    component: instance?.$options?.name,
  })
}

// 过滤已知无害的 Vue 警告，避免刷屏（不影响真实警告的输出）
// 「Component inside <Transition> renders non-element root node」：
//   media-fade 等 mode=out-in 过渡在 v-if 多视图切换的中间态会短暂渲染非元素根节点，
//   纯属动画提示、不影响功能，但每次重渲染都会打印，量极大，这里静音此条。
const _originalWarnHandler = app.config.warnHandler;
app.config.warnHandler = (msg, instance, trace) => {
  if (typeof msg === 'string') {
    if (msg.includes('renders non-element root node that cannot be animated')) {
      return;
    }
    // PlazaPage 已下线热门歌手页，避免每次播放状态更新刷屏
    if (msg.includes('showHotSingersPage')) {
      return;
    }
    // DXHPlayer 多根节点组件的已知 emits 警告
    if (msg.includes('Extraneous non-emits event listeners')) {
      return;
    }
  }
  if (_originalWarnHandler) {
    _originalWarnHandler(msg, instance, trace);
  } else {
    logger.warn('Vue', msg, trace)
  }
};

app.use(router)
app.use(Antd)
app.use(pinia)
app.use(uploader) // 注册上传组件

initMediaSession()

// 注册权限指令
app.directive('permission', permission)

// 判断是否为开发环境
const isDev = isDevelopmentMode()
logger.info('App', `当前环境: ${isDev ? '开发' : '生产'}`, {
  sessionId: observabilitySessionId,
  logLevel: logger.getMinLevel(),
})

// 开发环境导入缓存诊断工具
if (isDev) {
  import('@utils/cacheDiagnostic.js').then(() => {
    logger.info('App', '缓存诊断工具已加载', {
      commands: ['diagnoseCaches()', 'migrateCaches()', 'testMD5([songIds])', 'debugAudioController()'],
    })
  });
}

// 在应用启动时检查令牌有效性
// 先挂载、再异步拉取用户信息：fetchCurrentUser 内部还要串行等 /api/user/current、
// /api/modules 和 IndexedDB 缓存校验，一旦后端变慢，首屏会整段白屏。
const initApp = () => {
  // 在开发环境下，将用户store暴露到全局，方便调试
  if (isDev) {
    const userStore = useUserStore();
    window.userStore = userStore;
    window.__SESSION_ID__ = getSessionId();
    logger.debug('App', '开发调试入口已挂载', {
      userStore: 'window.userStore',
      sessionId: 'window.__SESSION_ID__',
    })
  }

  app.mount('#app')

  if (tokenService.hasAccessToken()) {
    logger.debug('App', '启动时检测到 accessToken，后台拉取用户信息')
    // 使用 userStore 的方法，确保缓存初始化被正确触发
    useUserStore().fetchCurrentUser().catch((error) => {
      logger.error('App', '启动时检查令牌失败', error)
    })
  } else if (tokenService.hasRefreshToken()) {
    logger.debug('App', '启动时检测到 refreshToken，等待拦截器刷新')
  }
}

// 启动应用
initApp();
